'use client';

import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import { trackToolResultAction } from '@/lib/analytics';

interface TrackedCopyButtonProps {
  /** Text to copy to clipboard */
  text: string;
  /** Tool slug for tracking */
  toolSlug: string;
  /** Button label (default: "Copy") */
  label?: string;
  /** Show label next to icon (default: true) */
  showLabel?: boolean;
  /** Additional className */
  className?: string;
  /** Button size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Button style variant */
  variant?: 'default' | 'outline' | 'ghost';
  /** Callback after successful copy */
  onCopy?: () => void;
}

/**
 * Copy button with built-in GA4 tracking.
 * Use this in tool outputs to track when users copy results.
 */
export default function TrackedCopyButton({
  text,
  toolSlug,
  label = 'Copy',
  showLabel = true,
  className = '',
  size = 'md',
  variant = 'default',
  onCopy,
}: TrackedCopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      // Track the copy action
      trackToolResultAction(toolSlug, 'copy');

      // Call optional callback
      onCopy?.();

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, [text, toolSlug, onCopy]);

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs gap-1',
    md: 'px-3 py-1.5 text-sm gap-1.5',
    lg: 'px-4 py-2 text-base gap-2',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Variant classes
  const variantClasses = {
    default: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    outline: 'border border-gray-300 hover:bg-gray-50 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-600',
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      title={copied ? 'Copied!' : `Copy ${label.toLowerCase()}`}
    >
      {copied ? (
        <>
          <Check className={`${iconSizes[size]} text-green-600`} />
          {showLabel && <span className="text-green-600">Copied!</span>}
        </>
      ) : (
        <>
          <Copy className={iconSizes[size]} />
          {showLabel && <span>{label}</span>}
        </>
      )}
    </button>
  );
}

/**
 * Hook for copying text with tracking
 * Use this when you need more control over the copy UI
 */
export function useTrackedCopy(toolSlug: string) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      // Track the copy action
      trackToolResultAction(toolSlug, 'copy');

      // Reset after 2 seconds
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch (error) {
      console.error('Failed to copy:', error);
      return false;
    }
  }, [toolSlug]);

  return { copied, copyToClipboard };
}
